// src/pages/organization/team/TeamFormPage.js

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from "react-router-dom";
import { useMainPageTitle } from "../../../contexts/PageContext";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";


const TeamFormPage = () => {
  const { t } = useTranslation();
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { updatePageTitle } = useMainPageTitle();
  const isEditMode = Boolean(teamId);

  const [formData, setFormData] = useState({
    name: "",
    type: "Under 18",
    gender: "male",
    uniqueName: "",
    logo: null,
  });

  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    updatePageTitle(
      isEditMode ? "Edit Team" : t("teams.createTeam"),
      isEditMode ? "Update your team information" : "Create a new team"
    );
  }, [isEditMode, updatePageTitle, t]);

  const fetchTeamData = useCallback(async () => {
    try {
      setLoading(true);
      const url = new URL(`${API_URL}/team/show-team-by-team-id`);
      url.searchParams.append("teamId", teamId);
      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to fetch team data");
        } else {
          throw new Error("Network response was not ok");
        }
      }

      const data = await response.json();
      setFormData({
        name: data.team.name,
        type: data.team.type,
        gender: data.team.gender,
        uniqueName: data.team.uniqueName,
      });
      if (data.team.logo) {
        const logoUrl = data.team.logo;
        setPreviewUrl(logoUrl);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (isEditMode) {
      fetchTeamData();
    }
  }, [teamId, isEditMode, fetchTeamData]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileChange(file);
  };

  const handleFileChange = (file) => {
    if (file) {
      if (file.type.startsWith("image/")) {
        setFormData((prev) => ({ ...prev, logo: file }));
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setError("Please upload an image file");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      const url = isEditMode
        ? `${API_URL}/team/edit-team?teamId=${teamId}`
        : `${API_URL}/team/create-team`;

      const response = await fetch(url, {
        method: isEditMode ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to save team");
        } else {
          throw new Error("Network response was not ok");
        }
      }

      const data = await response.json();
      console.log(data.team);
      navigate(`/organization/team/${data.team.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F15213]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
        {error && (
          <div className="bg-red-50 text-red-500 p-4 rounded-lg mb-4">
            {error}
          </div>
        )}

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Team Name
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            className="w-full px-4 py-2 rounded-lg border-0 ring-1 ring-gray-200 focus:ring-2 focus:ring-[#F15213] outline-none"
            required
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Unique Name
          </label>
          <input
            type="text"
            value={formData.uniqueName}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, uniqueName: e.target.value }))
            }
            className="w-full px-4 py-2 rounded-lg border-0 ring-1 ring-gray-200 focus:ring-2 focus:ring-[#F15213] outline-none"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, type: e.target.value }))
              }
              className="w-full px-4 py-2 rounded-lg border-0 ring-1 ring-gray-200 focus:ring-2 focus:ring-[#F15213] outline-none"
            >
              <option value="Under 18">Under 18</option>
              <option value="Senior">Senior</option>
              <option value="Professional">Professional</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Gender
            </label>
            <select
              value={formData.gender}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, gender: e.target.value }))
              }
              className="w-full px-4 py-2 rounded-lg border-0 ring-1 ring-gray-200 focus:ring-2 focus:ring-[#F15213] outline-none"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="mixed">Mixed</option>
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Team Logo
          </label>
          <div
            className={`border-2 border-dashed rounded-lg p-4 text-center ${
              isDragging
                ? "border-[#F15213] bg-[#F15213]/10"
                : "border-gray-300"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e.target.files[0])}
              className="hidden"
              id="logo-upload"
            />
            <label htmlFor="logo-upload" className="cursor-pointer">
              {previewUrl ? (
                <div className="relative w-32 h-32 mx-auto">
                  <img
                    src={previewUrl}
                    alt="Team logo preview"
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 hover:opacity-100 rounded-lg transition-opacity">
                    <span className="text-white text-sm">Change Logo</span>
                  </div>
                </div>
              ) : (
                <div className="text-gray-500">
                  <svg
                    className="mx-auto h-12 w-12"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p className="mt-1">Drag and drop or click to upload</p>
                </div>
              )}
            </label>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate("/organization/team/"+teamId)}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-[#F15213] text-white rounded-lg hover:bg-[#d14711] transition-colors disabled:opacity-50"
          >
            {loading ? "Saving..." : isEditMode ? "Update Team" : t("teams.createTeam")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeamFormPage;
